import LargePaidIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/paid';
import LargePriceTagIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/price-tag';
import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import BpkSectionHeader from '@skyscanner-internal/backpack-web/bpk-component-section-header';
import BpkText, {
  TEXT_COLORS,
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import classNames from 'classnames';

import withErrorBoundary from '../ErrorBoundary';
import { useMBDContext } from '../common/MBD/useMBDContext';

import type {
  VisualDataBarItem,
  VisualDataSnippetIcon,
  VisualDataSnippetItem,
  VisualDataProps,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/VisualDataProps';

import STYLES from './VisualData.module.scss';

const snippetIconMap: Record<VisualDataSnippetIcon, React.ComponentType> = {
  priceTag: LargePriceTagIcon,
  paid: LargePaidIcon,
};

const ADAPTIVE_COPY_INSIDE_THRESHOLD = 50;

const BarCopy = ({
  bar,
  isInsideHighlighted = false,
}: {
  bar: VisualDataBarItem;
  isInsideHighlighted?: boolean;
}) => {
  const textColor = isInsideHighlighted ? TEXT_COLORS.textOnDark : undefined;

  return (
    <div
      className={classNames(STYLES.Bar__copy, {
        [STYLES.Bar__copy_insideHighlighted]: isInsideHighlighted,
      })}
    >
      {bar.url ? (
        <BpkLink
          href={bar.url}
          className={STYLES.Bar__name}
          alternate={isInsideHighlighted}
        >
          {bar.name}
        </BpkLink>
      ) : (
        <BpkText
          textStyle={TEXT_STYLES.label1}
          color={textColor}
          className={STYLES.Bar__name}
        >
          {bar.name}
        </BpkText>
      )}
      <BpkText textStyle={TEXT_STYLES.footnote} color={textColor}>
        {bar.stat}
      </BpkText>
    </div>
  );
};

const Bar = ({ bar }: { bar: VisualDataBarItem }) => {
  const fillClass = bar.isHighlighted
    ? STYLES.Bar__fill_highlighted
    : STYLES.Bar__fill_regular;
  const clampedPercentage = Math.max(0, Math.min(100, bar.percentage));
  const usesAdaptiveCopy = bar.copyPlacement === 'adaptive';
  const isCopyInsideBar =
    usesAdaptiveCopy && clampedPercentage > ADAPTIVE_COPY_INSIDE_THRESHOLD;
  const fillClassName = classNames(fillClass, {
    [STYLES.Bar__fill_adaptive]: usesAdaptiveCopy,
  });

  return (
    <div className={STYLES.Bar}>
      <div className={fillClassName} style={{ width: `${clampedPercentage}%` }}>
        {isCopyInsideBar && (
          <div
            className={classNames(
              STYLES.Bar__copyWrapper,
              STYLES.Bar__copyWrapper_inside,
            )}
          >
            <BarCopy bar={bar} isInsideHighlighted={bar.isHighlighted} />
          </div>
        )}
      </div>
      {!isCopyInsideBar && (
        <div
          className={classNames(STYLES.Bar__logoSection, {
            [STYLES.Bar__logoSection_adaptive]: usesAdaptiveCopy,
          })}
        >
          {bar.imageUrl && (
            <img
              className={STYLES.Bar__image}
              src={bar.imageUrl}
              alt={bar.name}
            />
          )}
          <BarCopy bar={bar} />
        </div>
      )}
    </div>
  );
};

const InfoSnippet = ({ snippet }: { snippet: VisualDataSnippetItem }) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const IconComponent = snippetIconMap[snippet.icon];
  const snippetCtaComponentName = `${rawComponentName}_snippet_cta`;

  return (
    <div className={STYLES.InfoSnippet}>
      <div className={STYLES.InfoSnippet__icon}>
        {IconComponent && <IconComponent />}
      </div>
      <BpkText textStyle={TEXT_STYLES.bodyDefault}>{snippet.label}</BpkText>
      {snippet.url ? (
        <BpkLink
          href={snippet.url}
          className={STYLES.InfoSnippet__value}
          data-tracking-element-id={snippetCtaComponentName}
          data-tracking-common-params={buildMBDParam(snippetCtaComponentName)}
        >
          {snippet.value}
        </BpkLink>
      ) : (
        <BpkText
          textStyle={TEXT_STYLES.heading4}
          className={STYLES.InfoSnippet__value}
        >
          {snippet.value}
        </BpkText>
      )}
    </div>
  );
};

const VisualData = ({ sections }: VisualDataProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();

  return (
    <div
      className={STYLES.VisualData}
      data-testid="visualData"
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName, {
        version: 'v1',
      })}
    >
      {sections.map((section) => (
        <div key={section.header} className={STYLES.Section}>
          <BpkSectionHeader
            title={section.header}
            description={section.description}
          />
          <div className={STYLES.Section__content}>
            <div className={STYLES.BarChart}>
              {section.bars.map((bar) => (
                <Bar key={bar.name} bar={bar} />
              ))}
            </div>
            <div className={STYLES.SnippetPanel}>
              <div className={STYLES.SnippetPanel__grid}>
                {section.snippets.map((snippet) => (
                  <InfoSnippet key={snippet.label} snippet={snippet} />
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const VisualDataWithErrorBoundary = withErrorBoundary(
  VisualData,
  'visual-data-component',
);

export default VisualDataWithErrorBoundary;
